/**
 * The "Contact us to learn about …" call-to-action band near the end of many marketing pages: a
 * headline, an optional photo, and one or two cards. On the default copy the first card is
 * "Request a Quote" (`/contact-us#quoteRequest`) and the second is "Chat with a Metal
 * Professional", which opens the live-chat widget.
 *
 * Position: about 70 files reference this folder. Most render the default export
 * (`ContactUsSection`), often inside an `AllSections` list (for example
 * `components/BarTubeStructuralProcessing/BasePage/BasePage.tsx`,
 * `components/HomePage/STSHomepage.tsx`). Pages that need `isCareers`, and a few others, render
 * `./Desktop/ContactUs.tsx` and `./Mobile/ContactUs.tsx` directly (the careers pages under
 * `pages/about-us/careers/**`, `pages/who-we-are/index.tsx`,
 * `pages/metal-resources/references/index.tsx`,
 * `pages/metal-resources/tools-calculators/index.tsx`).
 * Content modules type their own copy with {@link ContactUsContent} (for example
 * `content/ryerson/metal-solutions/industries/index.ts`,
 * `components/Careers/ContactUs/Content.tsx`).
 *
 * Rules a change must keep:
 * - `ContactUsContent` is the shape every override in those content modules is written to, so a
 *   renamed or removed field breaks them at compile time; a new required field does too.
 * - `button1Link` / `button2Link` of `"/contact-us#quoteRequest"` depend on the contact form
 *   reading the hash as a topic:
 *   `components/ContactFormWithFileAttacher/ContactFormWithFile.Functions.tsx` →
 *   `getTopicFromHash`. An empty or missing link makes the card open the live chat instead
 *   (`./Shared/ContactCard.tsx`).
 *
 * This folder replaces `components/Shared/ContactUs/`, which has no importer.
 *
 * Related: `./Content.tsx` (Ryerson copy), `./STSContent.tsx` (Southern Tool Steel copy, dormant
 * build), `./Shared/ContactCard.tsx` (the card and its click behaviour).
 */
import React from "react";
import ContactUsDesktop from "./Desktop/ContactUs";
import { Media } from "@ryerson/frontend.next";
import { DefaultContactUsContent } from "./Content";
import { STSContactUsContent } from "./STSContent";
import ContactUsMobile from "./Mobile/ContactUs";
import { useApplication } from "@ryerson/frontend.application";

/** Props of {@link ContactUsSection}. */
export interface ContactUsProps {
	/** `ContentSection` background type. Default `"primary"`. */
	background?: "primary" | "secondary" | "tertiary";
	/** Show the photo layout (`content.imageUrl` beside stacked cards). Default `true`. */
	withImage?: boolean;
	/** Accepted and ignored: nothing in this folder reads it. */
	insideAccordion?: boolean;
	/** Render only the second card (the chat card on the default copy). Default `false`. */
	oneButtonVariant?: boolean;
	/**
	 * Copy for the band. When absent, the brand's default copy in the visitor's language is
	 * used.
	 */
	content?: ContactUsContent;
}

/**
 * Copy for one language of the contact band. Content modules across `src/content/` and
 * `components/` declare `LanguageContent<ContactUsContent>` objects in this shape.
 */
export type ContactUsContent = {
	/** Headline, rendered in the heading colour. */
	title: string;
	/**
	 * Rest of the headline, rendered in grey after `title`. On desktop, `\n` breaks lines
	 * (`white-space: pre-line`); below `lg` it collapses to a space.
	 */
	titleSpecial?: string;
	/** Section label. Not read by the components in this folder. */
	navLabel?: string;
	/** Photo for the `withImage` layout, a site-relative path. */
	imageUrl?: string;
	/** First card's text (the quote card on the default copy). */
	button1?: string;
	/** Second card's text (the chat card on the default copy). */
	button2?: string;
	/** First card's target, `path#hash`. Empty or absent makes the card open the live chat. */
	button1Link?: string;
	/** Second card's target, `path#hash`. Empty or absent makes the card open the live chat. */
	button2Link?: string;
	/**
	 * First card's text when `isCareers` is set ("Open Positions USA" in
	 * `components/Careers/ContactUs/Content.tsx`).
	 */
	newButton1?: string;
	/** Second card's text when `isCareers` is set ("Open Positions Canada"). */
	newButton2?: string;
};

/**
 * Props of the `./Desktop/ContactUs.tsx` and `./Mobile/ContactUs.tsx` layouts. All are required
 * except `isCareers`; {@link ContactUsSection} resolves the defaults before passing them on, and
 * direct callers pass every prop.
 */
export type ContactUsComponentProps = {
	content: ContactUsContent;
	withImage: boolean;
	background: "primary" | "secondary" | "tertiary";
	oneButtonVariant: boolean;
	/**
	 * Careers mode: the cards show `newButton1` / `newButton2` and link to
	 * `NEXT_PUBLIC_CAREERS_URL_USA` / `NEXT_PUBLIC_CAREERS_URL_CANADA` in a new tab, ignoring
	 * `button1Link` / `button2Link`. Two mobile cards only partly follow it (see
	 * `./Mobile/ContactUs.tsx`). Only callers that render a layout directly can set it;
	 * {@link ContactUsSection} doesn't pass it.
	 */
	isCareers?: boolean;
};

/**
 * Renders the contact band for both breakpoints: `./Mobile/ContactUs.tsx` below `lg` and
 * `./Desktop/ContactUs.tsx` from `lg` up. Without `content`, it picks `STSContactUsContent` on the
 * `southernToolSteel` build and `DefaultContactUsContent` otherwise, in the visitor's language.
 */
const ContactUsSection: React.FC<ContactUsProps> = ({
	background = "primary",
	withImage = true,
	oneButtonVariant = false,
	content,
}) => {
	const {
		localization: { language },
	} = useApplication();
	if (!content) {
		content =
			process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel"
				? STSContactUsContent[language]
				: DefaultContactUsContent[language];
	}
	return (
		<>
			<Media lessThan="lg">
				<ContactUsMobile
					content={content}
					withImage={withImage}
					background={background}
					oneButtonVariant={oneButtonVariant}
				/>
			</Media>
			<Media greaterThanOrEqual="lg">
				<ContactUsDesktop
					content={content}
					withImage={withImage}
					background={background}
					oneButtonVariant={oneButtonVariant}
				/>
			</Media>
		</>
	);
};

export default ContactUsSection;
